import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TransactionNumberSeries = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [editData, setEditData] = useState({
    prefix: '',
    nextNumber: 1,
    format: '',
    resetPeriod: 'Never'
  });

  const resetPeriodOptions = ['Never', 'Monthly', 'Quarterly', 'Yearly'];

  // TODO: Load series from database
  React.useEffect(() => {
    const loadSeries = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API call to load transaction number series
        // const data = await transactionSeriesService.getAll();
        // setSeries(data);
        setSeries([]);
      } catch (err) {
        setError(err?.message || 'Failed to load transaction number series');
      } finally {
        setLoading(false);
      }
    };

    loadSeries();
  }, []);

  const handleEditSeries = (seriesItem) => {
    setEditingSeries(seriesItem);
    setEditData({
      prefix: seriesItem?.prefix,
      nextNumber: seriesItem?.nextNumber,
      format: seriesItem?.format,
      resetPeriod: seriesItem?.resetPeriod
    });
    setShowEditModal(true);
  };

  const handleUpdateSeries = () => {
    setSeries(prev => 
      prev?.map(item => 
        item?.id === editingSeries?.id 
          ? {
              ...item,
              prefix: editData?.prefix,
              nextNumber: editData?.nextNumber,
              format: editData?.format,
              resetPeriod: editData?.resetPeriod,
              example: generateExample(editData?.format, editData?.nextNumber, editData?.prefix)
            }
          : item
      )
    );
    setShowEditModal(false);
    setEditingSeries(null);
  };

  const generateExample = (format, number, prefix) => {
    if (!format || !number) return '';
    
    // Replace placeholder with actual number
    const numberStr = number?.toString();
    const paddedNumber = numberStr?.padStart(4, '0'); // Default to 4 digits
    
    return format?.replace('{0000}', paddedNumber)
                  ?.replace('{000}', numberStr?.padStart(3, '0'))
                  ?.replace('{00}', numberStr?.padStart(2, '0'));
  };

  const toggleSeriesStatus = (seriesId) => {
    setSeries(prev => 
      prev?.map(item => 
        item?.id === seriesId ? { ...item, isActive: !item?.isActive } : item
      )
    );
  };

  const resetSeries = (seriesId) => {
    const confirmed = window.confirm('Are you sure you want to reset this series to 1? This cannot be undone.');
    if (!confirmed) return;

    setSeries(prev => 
      prev?.map(item => 
        item?.id === seriesId 
          ? { 
              ...item, 
              currentNumber: 0, 
              nextNumber: 1,
              example: generateExample(item?.format, 1, item?.prefix)
            } 
          : item
      )
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="Loader2" size={24} className="animate-spin mr-2" />
        <span>Loading transaction series...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={16} className="text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Transaction Number Series</h2>
          <p className="text-muted-foreground">Configure automatic numbering for invoices, quotes, and other business documents</p>
        </div>
      </div>

      {/* Series List */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-foreground">Document Type</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Prefix</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Current Number</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Next Number</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Format</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Example</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Reset Period</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {series?.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-muted-foreground">
                    <div className="flex flex-col items-center space-y-2">
                      <Icon name="FileText" size={48} className="text-muted-foreground/50" />
                      <p>No transaction number series configured</p>
                      <p className="text-sm">Configure automatic numbering for your business documents</p>
                    </div>
                  </td>
                </tr>
              ) : (
                series?.map((item) => (
                  <tr key={item?.id} className="border-t border-border hover:bg-muted/20">
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="FileText" size={16} className="text-muted-foreground" />
                        <span className="font-medium text-foreground">{item?.type}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {item?.prefix}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-foreground">{item?.currentNumber}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-medium text-foreground">{item?.nextNumber}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-sm text-muted-foreground">{item?.format}</span>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-sm bg-green-50 text-green-700 px-2 py-1 rounded">
                        {item?.example}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">{item?.resetPeriod}</span>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => toggleSeriesStatus(item?.id)}
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                          item?.isActive 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          item?.isActive ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        {item?.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditSeries(item)}
                          iconName="Edit"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resetSeries(item?.id)}
                          iconName="RotateCcw"
                        >
                          Reset
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Series Configuration Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-2">Number Series Configuration Tips</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Use {'{0000}'} for 4-digit zero-padded numbers (e.g., 0001, 0002)</li>
              <li>• Use {'{000}'} for 3-digit zero-padded numbers</li>
              <li>• Include prefixes to categorize different document types</li>
              <li>• Reset periods help organize documents by time periods</li>
              <li>• Inactive series won't generate new numbers but existing documents remain valid</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Edit Series Modal */}
      {showEditModal && editingSeries && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Edit {editingSeries?.type} Series
              </h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowEditModal(false)}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <Input
                label="Prefix"
                value={editData?.prefix}
                onChange={(e) => setEditData(prev => ({ ...prev, prefix: e?.target?.value }))}
                placeholder="Enter prefix"
              />

              <Input
                label="Next Number"
                type="number"
                value={editData?.nextNumber}
                onChange={(e) => setEditData(prev => ({ ...prev, nextNumber: parseInt(e?.target?.value) || 1 }))}
                min="1"
              />

              <Input
                label="Format"
                value={editData?.format}
                onChange={(e) => setEditData(prev => ({ ...prev, format: e?.target?.value }))}
                placeholder="e.g., INV-{0000}"
                helperText="Use {0000} for 4-digit numbers, {000} for 3-digit numbers"
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Reset Period
                </label>
                <select
                  value={editData?.resetPeriod}
                  onChange={(e) => setEditData(prev => ({ ...prev, resetPeriod: e?.target?.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {resetPeriodOptions?.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Preview */}
              <div className="bg-muted rounded-lg p-3">
                <label className="text-sm font-medium text-foreground mb-1 block">
                  Preview
                </label>
                <span className="font-mono text-sm bg-green-50 text-green-700 px-2 py-1 rounded">
                  {generateExample(editData?.format, editData?.nextNumber, editData?.prefix) || 'Invalid format'}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button 
                variant="outline" 
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateSeries}
                iconName="Save"
              >
                Update Series
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionNumberSeries;